'use client'

import {useTranslate} from '@local/hooks'
import {SanityImageType, VideoOrImageType} from '@local/types'
import {Button, Pastil, SmallText, VideoOrImage} from '@local/ui/components'
import {cx} from '@local/utils/cx'
import {ForwardedRef, forwardRef, useRef} from 'react'
import {formatPrice} from '@local/utils/currency'

export interface ProductPreviewProps {
	store: {
		tags: string,
		productTag?: string,
		variants: Array<{type: string}>
		createdAt: string
		priceRange: {
			minVariantPrice: string
			maxVariantPrice: string
		}
	}
	_createdAt: string
	slug: string
	title: string
	slugPrefix: 'experience' | 'merch'
	excerpt?: string
	location?: string
	datePeriod?: string
	productType?: string
}

type TagTypes = 'kaer-choice' | 'coming-soon'

export interface ProductSliderCardProps {
	className?: string
	media: VideoOrImageType
	image?: SanityImageType
	title: string
	slug: string
	item: ProductPreviewProps
	locationLabel: string
	index: number
	id: string
	tag?: TagTypes
	type?: string
	disabled?: boolean
	disabledMessageList?: boolean
	disabledMessageCard?: boolean
	disabledMessageCardMobile?: boolean
	notifyClick?: () => void
	getRef?: (HTMLElement) => void
}

export const ProductSliderCard = forwardRef(
	(
		{
			id,
			tag,
			slug,
			item,
			index,
			media,
			disabled,
			className,
			locationLabel,
			disabledMessageCard,
			disabledMessageCardMobile,
			notifyClick,
			getRef,
		}: ProductSliderCardProps,
		ref: ForwardedRef<HTMLDivElement>,
	) => {
		const locale = useTranslate()
		const isSmall = index % 2 !== 0 && index < 5
		const isComingSoon = tag === 'coming-soon'

		const setRefs = (node: HTMLElement) => {
			if (ref) ref.current = node
			getRef?.(node)
		}

		const handleClick = (e) => {
			e.preventDefault()
			e.stopPropagation()
			notifyClick?.()
		}

		return (
			<li
				data-id={id}
				ref={setRefs}
				className={cx(
					'shrink-0 flex flex-row items-end justify-center relative span-w-7 lg:w-[18.6vw] overflow-clip group rounded-lg',
					'lg-wh:w-[28vw] lg-wh:max-h-[70vh]',
					isSmall
						? 'lg-max:h-240 lg:aspect-[268/300] lg-wh:max-h-[50vh]'
						: 'lg-max:h-300 lg:aspect-[268/400]',
					className,
				)}>
				<Button
					href={slug}
					disabled={disabled}
					className={cx(
						disabled && 'hidden',
						'group/btn outline-none',
					)}>
					<VideoOrImage
						image={media?.image}
						video={media?.video}
						selectedTab={media?.selectedTab}
						width={268}
						height={400}
						desktopWidth={268}
						mobileWidth={200}
						ratioMobile={200 / 300}
						priority={index < 2}
						videoProps={{fill: true}}
						className={cx(
							'span-w-7 lg:w-[18.6vw] lg-wh:w-[28vw] lg-wh:max-h-[70vh] object-cover [&_video]:object-cover',
							isSmall ? 'lg-max:h-240 lg:aspect-[268/300] lg-wh:max-h-[50vh]' : 'lg-max:h-300 lg:aspect-[268/400]',
							'scale-110 group-hover:scale-100 group-focus-visible/btn:scale-100 transition-transform duration-700 ease-[cubic-bezier(.35,0,.19,1)]',
						)} />
					<div
						className={cx(
							'absolute inset-0 flex flex-col items-center justify-center text-white bg-black/30 px-12 lg:px-30',
							'transition-[backdrop-filter,background-color] transform-gpu duration-700 ease-quad-out group-hover:backdrop-blur-sm group-hover:bg-black/50',
							'group-focus-visible/btn:backdrop-blur-sm group-focus-visible/btn:bg-black/50',
						)}>
						<h3 className='font-serif capitalize text-16 lg:text-20 lg-wh:text-34 leading-snug mb-6 lg-wh:mb-18'>{item.title}</h3>
						{item.slugPrefix === 'experience' && item.location && (
							<SmallText textColor='white' className='lg-wh:text-14 lg-wh:pb-7'>
								<span>{locationLabel}: </span>
								<span>{item.location}</span>
							</SmallText>
						)}
						{item.slugPrefix === 'merch' && (
							<SmallText textColor='white' className='lg-wh:text-14'>
								<span>{item.productType}</span>
							</SmallText>
						)}
						{item.slugPrefix === 'experience' && item.datePeriod?.trim() && (
							<SmallText textColor='white' className='lg-wh:text-14'>{item.datePeriod}</SmallText>
						)}
						{isComingSoon && (
							<>
								<Pastil
									className='mt-10 lg-wh:mt-18 lg-wh:text-12 lg-wh:p-7'
									variant='grey'
									onClick={handleClick}>
									{locale('Coming Soon')}
								</Pastil>
								<Button
									tabIndex={0}
									type='button'
									onClick={handleClick}
									onKeyDown={e => { if (e.key === 'Enter') handleClick(e) }}
									as='div'
									variant='underlined'
									active
									className={cx(
										'uppercase font-medium pt-10 lg-wh:mt-18 after:translate-y-2 overflow-visible',
										'hover:after:scale-x-0 hover:after:origin-right',
										'focus-visible:after:scale-x-0 focus-visible:after:origin-right lg-wh:text-14',
									)}>
									{locale('Notify Me')}
								</Button>
							</>
						)}
						{tag === 'kaer-choice' && <Pastil className='absolute top-20'>{locale('Kaer Choice')}</Pastil>}
						{item.excerpt && item.store?.priceRange?.minVariantPrice
							? (
								<SmallText textColor='white' className='lg-wh:text-14 absolute bottom-20'>
									{item.excerpt + formatPrice(item.store.priceRange.minVariantPrice, 'nl-NL', undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0}).replace(/\s/g, '')}
								</SmallText>
							)
							: <></>}
					</div>

				</Button>
				<div className={cx(
					'grid place-content-center text-center bg-grey-5/5 h-full w-full px-12 lg:px-32',
					!disabled && 'hidden',
				)}>
					{disabledMessageCard && (
						<h3 className='lg-max:hidden font-serif lg:text-20 leading-snug text-grey-4'>
							{disabledMessageCard}
						</h3>
					)}
					{disabledMessageCardMobile && (
						<h3 className='lg:hidden font-serif text-16 leading-snug text-grey-4 span-w-5'>
							{disabledMessageCardMobile}
						</h3>
					)}
				</div>
			</li>
		)
	},
)
